import { Component, Input } from '@angular/core';
import { formatDate } from '@angular/common';
import { scaleLinear, scaleTime } from 'd3-scale';
import { area, line } from 'd3-shape';
import { Debounce } from 'lodash-decorators';
import {
  ComponentVisibilityService,
  HistoricalService
} from '@approot/services';

@Component({
  selector: 'app-historical-graph',
  templateUrl: './historical-graph.component.html',
  styleUrls: ['./historical-graph.component.scss']
})
export class HistoricalGraphComponent {
  @Input() currencyPairs: string[];
  @Input() widgetId: number;
  noDataMessage: string;
  data: any[];

  instruments = {
    Spot: 'SpotMid',
    'Forward Points': 'ForwardMid'
  };

  instrumentsOptions = ['Spot', 'Forward Points'];
  selectedInstrument = 'SpotMid';

  tenors = {
    ON: 'ON',
    '1W': '1w',
    '2W': '2w',
    '3W': '3w',
    '1M': '1m',
    '2M': '2m',
    '3M': '3m',
    '6M': '6m',
    '9M': '9m',
    '1Y': '12m'
  };

  tenorsArray = ['ON', '1W', '2W', '3W', '1M', '2M', '3M', '6M', '9M', '1Y'];
  selectedTenor = 'ON';

  deltas = {
    d1: 'd1',
    d2: 'd2',
    d3: 'd3'
  };
  selectedDelta = 'd1';

  sides = {
    Bid: 'bid',
    Ask: 'ask'
  };
  selectedSide = 'bid';

  graphWidth = 600;
  graphHeight = 200;
  areaCoords = '';
  lineCoords = '';
  xTicks = [];
  yTicks = [];

  _selectedCurrencyPair: any;

  constructor(
    private componentVisibilityService: ComponentVisibilityService,
    private historicalService: HistoricalService
  ) {}

  closeWidget() {
    this.componentVisibilityService.removeWidget('his', this.widgetId);
  }

  get selectedCurrencyPair() {
    return this._selectedCurrencyPair;
  }

  set selectedCurrencyPair(value) {
    this._selectedCurrencyPair = value;
  }

  onInstrumentChanged(value: string) {
    this.selectedInstrument = this.instruments[value];
    this.selectedTenor = '';
    this.setGraphEmpty();
    this.getHistoricalData(this.selectedInstrument, this.selectedCurrencyPair);
  }

  onTenorChanged(value: string) {
    this.selectedTenor = value;
    this.setGraphEmpty();
    this.calcGraphData();
  }

  onCurrencyPairChanged(value: string) {
    this.selectedCurrencyPair = value;
    this.setGraphEmpty();
    this.getHistoricalData(this.selectedInstrument, this.selectedCurrencyPair);
  }

  @Debounce(250)
  getHistoricalData(instrument, cross) {
    if (instrument && cross) {
      this.noDataMessage = '';
      this.historicalService.getHistorical(instrument, cross).then(p => {
        p.subscribe(
          (resp: any[]) => {
            if (resp.length) {
              this.data = resp;
              this.calcGraphData();
            } else {
              this.noDataMessage = 'No data found.';
              this.setGraphEmpty();
            }
          },
          (err: any) => {
            console.error('getHistoricalData', JSON.stringify(err));
          }
        );
      });
    }
  }

  setGraphEmpty() {
    this.areaCoords = null;
    this.lineCoords = null;
    this.yTicks = null;
    this.xTicks = null;
  }

  // TODO make the graphs width dynamic:
  // https://medium.com/@jeanphilippelemieux/creating-a-responsive-graph-with-angular-and-d3-b45bb8065588
  calcGraphData() {
    let graphData = [];
    if (this.selectedInstrument === 'ForwardMid') {
      graphData = this.data.filter(x => {
        if (x.tenor && x.tenor[0] === '0') {
          x.tenor = x.tenor.substr(1);
        } else if (x.tenor === '12M') {
          x.tenor = '1Y';
        }
        return x.tenor === this.selectedTenor;
      });
    } else {
      graphData = this.data;
    }

    if (graphData.length) {
      const dataArray = graphData.map(d => ({
        x: new Date(d.timestamp),
        y: Number(d.price)
      }));

      const minX = dataArray[0].x;
      const maxX = dataArray[dataArray.length - 1].x;

      const y = dataArray.map(d => d.y);
      // the destructuring assignment syntax is a JavaScript expression that makes it
      // possible to extract data from arrays or objects into distinct variables.
      const minY = Math.min(...y);
      const maxY = Math.max(...y);

      const scaleX = scaleTime()
        .domain([minX, maxX])
        .range([0, this.graphWidth]);

      const scaleY = scaleLinear()
        .domain([minY, maxY])
        .range([this.graphHeight, 0]);

      const toAreaCoords = area()
        .x(d => scaleX(d.x))
        .y1(d => scaleY(d.y))
        .y0(this.graphHeight);

      this.areaCoords = toAreaCoords(dataArray);

      const toLineCoords = line()
        .x(d => scaleX(d.x))
        .y(d => scaleY(d.y));

      this.lineCoords = toLineCoords(dataArray);

      this.xTicks = scaleX.ticks(4).map(tick => {
        return { tick, x: scaleX(tick) };
      });
      this.yTicks = scaleY.ticks(6).map(tick => {
        return { tick, y: scaleY(tick) };
      });
    }
  }

  formatDate(value: Date | number) {
    return formatDate(value, 'd MMM y', 'en');
  }

  pairToLabel(pair: string) {
    return pair ? pair.replace(/(\w{3})(\w{3})/g, '$1/$2') : '';
  }
}
